import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatApiError, formatDate, formatDateMSK, formatCurrency } from '@/lib/api';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import OverviewPanel from '@/components/OverviewPanel';
import TenderAttachments from '@/components/TenderAttachments';
import ProductSelector from '@/components/ProductSelector';
import TenderMonitor from '@/components/TenderMonitor';
import ShiftDatesDialog from '@/components/ShiftDatesDialog';
import AdminInvitationsLog from '@/components/AdminInvitationsLog';
import InvitationLinksPanel from '@/components/InvitationLinksPanel';
import PriceCalculator from '@/components/PriceCalculator';
import ArchivePanel from '@/components/ArchivePanel';
import ProductsManager from '@/components/ProductsManager';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Send, Save, Trash2, Search, FileText, Users, Settings,
  ChevronDown, ChevronUp, Award, Eye, Clock, CheckCircle2, Wifi, WifiOff,
  X, Ban, FastForward, RefreshCw, Pencil, Copy, LayoutDashboard, Upload, AlertCircle, CalendarClock,
  Calculator, Archive, Package
} from 'lucide-react';

const DEFAULT_CURRENCIES = [{ code: 'USD' }, { code: 'EUR' }, { code: 'RUB' }, { code: 'CNY' }];
const DEFAULT_UNITS = [{ code: 'т' }, { code: 'шт' }, { code: 'кг' }, { code: 'л' }];

const PHASE_MAP = {
  draft: { label: 'Черновик', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  published: { label: 'Опубликован', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  waiting: { label: 'Ожидание', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  collecting: { label: 'Сбор заявок', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  waiting_bids: { label: 'Ожидание торгов', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  bidding: { label: 'Торги', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  completed: { label: 'Завершён', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  cancelled: { label: 'Отменён', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const emptyLot = {
  lot_name: '', buying_company: '', product_name: '', product_name_en: '', product_id: null,
  volume: '', volume_unit: 'т', expected_price: '',
  currency: 'USD', money_cost_day: '', logistics_cost_per_container: '', max_container_weight: '25',
  description: '', app_start: '', app_end: '', bid_start: '', bid_end: '',
  step_type: 'fixed', step_value: '', step_from_own_bid: false, invited_emails: [],
  supplier_logistics: {},
  // 1C ERP — импортные параметры
  is_import: false,
  duty_rate: '', vat_rate: '', customs_fee: '',
  supplier_freight: {}, supplier_post_customs: {}, supplier_transit_days: {},
  // Направление лота: только супер-админ может выбрать; обычный organizer наследует.
  direction_override: '',
  // Альтернативные предложения (только для direction=purchase_oil).
  allow_alternative_bids: false,
  // Скрыть конкурентов от участников в ранжировании.
  hide_participants: false,
};

// Преобразует ISO-дату с таймзоной (+03:00) в формат для input[type=datetime-local] (YYYY-MM-DDTHH:mm)
// Сохраняем именно локальные (МСК) часы, а не UTC — пользователь работает в МСК.
const isoToDatetimeLocal = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
};

export default function OrganizerDashboard() {
  const { user } = useAuth();
  const isPurchaseOrg = user?.tender_direction === 'purchase';
  const isLogisticsOrg = user?.tender_direction === 'logistics';
  const isOilOrg = user?.tender_direction === 'purchase_oil';
  const isSuperAdmin = !!user?.is_super_admin || (user?.email || '').toLowerCase() === 'supply@chemdel.net';
  // «Вид поставщика» имеет смысл только для закупочных направлений (purchase, purchase_oil)
  // или для супер-админа, чтобы он мог заводить любых поставщиков.
  const isPurchaseDir = isPurchaseOrg || isOilOrg || isSuperAdmin;
  // Ценовой потолок обязателен для всех, кроме organizer-логистов:
  // для них это поле опционально (часто потолка просто нет в логистических лотах).
  const priceCapRequired = !isLogisticsOrg;
  const [activeTab, setActiveTab] = useState('overview');
  const [lot, setLot] = useState({ ...emptyLot });
  const [suppliers, setSuppliers] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [selectedTender, setSelectedTender] = useState(null);
  const [bids, setBids] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ company_name: '', email: '', contact_name: '', country: '', lang_code: 'RU', responsible: '', supplier_products: '', supplier_kind: '' });
  const [importingSuppliers, setImportingSuppliers] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');

  const filteredSuppliers = useMemo(() => {
    const q = directorySearch.trim().toLowerCase();
    const byName = (a, b) =>
      (a.company_name || '').localeCompare(b.company_name || '', 'ru', { sensitivity: 'base' });
    if (!q) return [...suppliers].sort(byName);
    return suppliers.filter(s => {
      const fields = [
        s.company_name, s.email, s.contact_name, s.country,
        s.responsible, s.lang_code,
        Array.isArray(s.supplier_products) ? s.supplier_products.join(' ') : (s.supplier_products || ''),
        s.supplier_kind === 'manufacturer' ? 'производитель' :
          s.supplier_kind === 'trader' ? 'трейдер' : '',
      ];
      return fields.some(v => (v || '').toString().toLowerCase().includes(q));
    }).sort(byName);
  }, [suppliers, directorySearch]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [renego, setRenego] = useState(null);
  const [shiftDates, setShiftDates] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState(''); // {tenderId, bid_start, bid_end, additional_conditions}
  const [editingTenderId, setEditingTenderId] = useState(null);
  const [planResult, setPlanResult] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [highlightTenderId, setHighlightTenderId] = useState(null);
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const tenderRefs = useRef({});

  // WebSocket для мониторинга ставок в реальном времени
  const { lastMessage, isConnected } = useWebSocket(selectedTender?.id);

  // Обработка WebSocket обновлений
  useEffect(() => {
    if (lastMessage?.type === 'bid_update' && selectedTender && lastMessage.tender_id === selectedTender.id) {
      setBids(lastMessage.organizer_bids || []);
    }
  }, [lastMessage, selectedTender]);

  const loadSuppliers = useCallback(async () => {
    try {
      const { data } = await api.get('/suppliers');
      setSuppliers(data);
    } catch (_e) { /* noop */ }
  }, []);

  const loadTenders = useCallback(async () => {
    try {
      const { data } = await api.get('/tenders', { params: { search } });
      setTenders(data);
    } catch (_e) { /* noop */ }
  }, [search]);

  useEffect(() => { loadSuppliers(); loadTenders(); }, [loadSuppliers, loadTenders]);

  // Загружаем единицы измерения и валюты из админ-справочника (с fallback на дефолты).
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([api.get('/units'), api.get('/currencies')]);
        if (Array.isArray(u.data) && u.data.length) setUnits(u.data);
        if (Array.isArray(c.data) && c.data.length) setCurrencies(c.data);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  // Настройки метрик (auto_invite_by_product, block_publish_unassigned и т.д.).
  const [metricsSettings, setMetricsSettings] = useState({
    auto_invite_by_product: true, block_publish_unassigned: true,
  });
  useEffect(() => {
    api.get('/admin/metrics-settings')
      .then(({ data }) => setMetricsSettings(prev => ({ ...prev, ...(data || {}) })))
      .catch(() => {});
  }, []);

  // Список организаторов для выбора «ответственного» в справочнике поставщиков.
  const [organizers, setOrganizers] = useState([]);
  useEffect(() => {
    api.get('/organizers').then(({ data }) => setOrganizers(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Автозаполнение участников по совпадению продукта (только purchase + is_import).
  // Не использует toggleEmail: только добавляет, не снимает уже отмеченных.
  useEffect(() => {
    if (!isPurchaseOrg) return;
    if (!lot.is_import) return;
    if (!metricsSettings.auto_invite_by_product) return;
    const productName = (lot.product_name || '').trim().toLowerCase();
    if (!productName) return;
    if (!Array.isArray(suppliers) || suppliers.length === 0) return;
    const toAdd = [];
    for (const s of suppliers) {
      if (s.stop_list) continue;
      const prods = Array.isArray(s.supplier_products) ? s.supplier_products : [];
      const hit = prods.some(p => (p || '').trim().toLowerCase() === productName);
      if (!hit) continue;
      if (!lot.invited_emails.includes(s.email)) toAdd.push(s.email);
    }
    if (toAdd.length === 0) return;
    setLot(p => ({
      ...p,
      invited_emails: [...p.invited_emails, ...toAdd.filter(e => !p.invited_emails.includes(e))],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lot.product_name, lot.product_id, lot.is_import, metricsSettings.auto_invite_by_product, suppliers]);


  // Открытие тендера по ?tender=<id> в URL (например, при переходе из аналитики
  // двойным кликом по строке лота). Если тендер не в списке (например, архивный
  // completed) — подгружаем напрямую через GET /tenders/{id}.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get('tender');
    if (!target) return;
    (async () => {
      let t = tenders.find(x => x.id === target);
      if (!t) {
        try {
          const { data } = await api.get(`/tenders/${target}`);
          t = data;
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || 'Тендер недоступен');
        }
      }
      if (t) {
        setSelectedTender(t);
        setActiveTab('tenders');
        // Скроллим к модалке мониторинга после ререндера
        setTimeout(() => {
          const el = document.querySelector('[data-testid="bid-monitoring"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 250);
      }
      // Чистим query, чтобы при закрытии модалки она не открылась снова
      const next = new URLSearchParams(searchParams);
      next.delete('tender');
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tenders.length]);

  // Live-обновление: когда супер-админ удалил тендер, WS присылает событие.
  // Сразу убираем его из списка (оптимистично) и закрываем модалку, если открыта.
  useEffect(() => {
    const handler = (e) => {
      const tid = e.detail?.tender_id;
      if (!tid) return;
      setTenders(prev => prev.filter(t => t.id !== tid));
      setSelectedTender(prev => (prev && prev.id === tid ? null : prev));
    };
    window.addEventListener('tender:removed', handler);
    return () => window.removeEventListener('tender:removed', handler);
  }, []);

  // Сортировка тендеров: 1) активные сверху (collecting/bidding/waiting/waiting_bids),
  // 2) внутри группы — по дате «Начала заявок» от поздних к ранним.
  // Тендеры без app_start уходят в конец своей группы.
  const sortedTenders = useMemo(() => {
    const ACTIVE_PHASES = ['waiting', 'collecting', 'waiting_bids', 'bidding'];
    return [...tenders].sort((a, b) => {
      const aActive = ACTIVE_PHASES.includes(a.phase) ? 0 : 1;
      const bActive = ACTIVE_PHASES.includes(b.phase) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      const ta = a.app_start ? new Date(a.app_start).getTime() : -Infinity;
      const tb = b.app_start ? new Date(b.app_start).getTime() : -Infinity;
      return tb - ta;
    });
  }, [tenders]);

  // Открытие тендера из календаря: переключаем вкладку, скроллим к карточке, временно подсвечиваем.
  // viewBids определён ниже — используем имя через замыкание (callback пересоздаётся при необходимости).

  useEffect(() => {
    if (!highlightTenderId || activeTab !== 'tenders') return;
    // Дожидаемся рендера карточек.
    const timer = setTimeout(() => {
      const el = tenderRefs.current[highlightTenderId];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);
    const clearTimer = setTimeout(() => setHighlightTenderId(null), 3200);
    return () => { clearTimeout(timer); clearTimeout(clearTimer); };
  }, [highlightTenderId, activeTab, sortedTenders]);

  // ====== Create Lot ======
  const updateLot = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setLot(p => ({ ...p, [key]: val }));
  };

  const toggleEmail = (email) => {
    setLot(p => {
      const isInvited = p.invited_emails.includes(email);
      const next = { ...p };
      if (isInvited) {
        next.invited_emails = p.invited_emails.filter(e => e !== email);
        // удаляем индивидуальную логистику для исключённого поставщика
        const sl = { ...(p.supplier_logistics || {}) };
        delete sl[email];
        delete sl[(email || '').toLowerCase()];
        next.supplier_logistics = sl;
      } else {
        next.invited_emails = [...p.invited_emails, email];
        // Авто-инициализация индивидуальной логистики дефолтом из общего поля,
        // чтобы при публикации валидация не падала «не заполнено».
        const slKey = (email || '').toLowerCase();
        const sl = { ...(p.supplier_logistics || {}) };
        if (!sl[slKey] && p.logistics_cost_per_container) {
          sl[slKey] = String(p.logistics_cost_per_container);
          next.supplier_logistics = sl;
        }
      }
      return next;
    });
  };

  const setSupplierLogistics = (email, value) => {
    setLot(p => ({
      ...p,
      supplier_logistics: { ...(p.supplier_logistics || {}), [email.toLowerCase()]: value }
    }));
  };

  // 1C ERP — установка индивидуальных импортных параметров (freight / post_customs / transit_days).
  const setSupplierImportField = (fieldKey) => (email, value) => {
    setLot(p => ({
      ...p,
      [fieldKey]: { ...(p[fieldKey] || {}), [email.toLowerCase()]: value }
    }));
  };
  const setSupplierFreight = setSupplierImportField('supplier_freight');
  const setSupplierPostCustoms = setSupplierImportField('supplier_post_customs');
  const setSupplierTransitDays = setSupplierImportField('supplier_transit_days');

  const handleCreateLot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Сериализуем supplier_logistics: '' -> 0 (или пропускаем); только числа.
      const slPayload = {};
      Object.entries(lot.supplier_logistics || {}).forEach(([k, v]) => {
        const num = parseFloat(v);
        if (!isNaN(num)) slPayload[(k || '').toLowerCase().trim()] = num;
      });
      // 1C ERP — сериализация per-supplier импортных параметров.
      const serializeMap = (src, isInt = false) => {
        const out = {};
        Object.entries(src || {}).forEach(([k, v]) => {
          const num = isInt ? parseInt(v, 10) : parseFloat(v);
          if (!isNaN(num)) out[(k || '').toLowerCase().trim()] = num;
        });
        return out;
      };
      const payload = {
        ...lot,
        volume: parseFloat(lot.volume) || 0,
        expected_price: parseFloat(lot.expected_price) || 0,
        money_cost_day: parseFloat(lot.money_cost_day) || 0,
        logistics_cost_per_container: parseFloat(lot.logistics_cost_per_container) || 0,
        max_container_weight: parseFloat(lot.max_container_weight) || 25,
        step_value: parseFloat(lot.step_value) || 0,
        step_from_own_bid: !!lot.step_from_own_bid,
        supplier_logistics: slPayload,
        // 1C ERP
        is_import: !!lot.is_import,
        duty_rate: parseFloat(lot.duty_rate) || 0,
        vat_rate: parseFloat(lot.vat_rate) || 0,
        customs_fee: parseFloat(lot.customs_fee) || 0,
        supplier_freight: serializeMap(lot.supplier_freight),
        supplier_post_customs: serializeMap(lot.supplier_post_customs),
        supplier_transit_days: serializeMap(lot.supplier_transit_days, true),
        // Направление и альтернативные предложения
        direction_override: isSuperAdmin ? (lot.direction_override || '') : '',
        allow_alternative_bids: !!lot.allow_alternative_bids,
        hide_participants: !!lot.hide_participants,
      };
      let saved = null;
      if (editingTenderId) {
        const r = await api.put(`/tenders/${editingTenderId}`, payload);
        saved = r?.data;
        toast.success('Изменения сохранены');
      } else {
        const r = await api.post('/tenders', payload);
        saved = r?.data;
        toast.success('Лот создан');
      }
      // Уведомление о не распределённых участниках (только purchase + is_import).
      if (saved && Array.isArray(saved.unassigned_participants) && saved.unassigned_participants.length > 0) {
        toast.warning(
          'Внимание: следующие участники не закреплены за организатором. Для публикации тендера необходимо назначить им ответственного: ' +
          saved.unassigned_participants.join(', '),
          { duration: 10000, id: `unassigned-${saved.id || 'new'}` }
        );
      }
      setLot({ ...emptyLot });
      setEditingTenderId(null);
      loadTenders();
      setActiveTab('tenders');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleEditTender = (tender) => {
    setEditingTenderId(tender.id);
    setLot({
      lot_name: tender.lot_name || '',
      buying_company: tender.buying_company || '',
      product_name: tender.product_name || '',
      product_name_en: tender.product_name_en || '',
      product_id: tender.product_id || null,
      volume: tender.volume ?? '',
      volume_unit: tender.volume_unit || 'т',
      expected_price: tender.expected_price ?? '',
      currency: tender.currency || 'USD',
      money_cost_day: tender.money_cost_day ?? '',
      logistics_cost_per_container: tender.logistics_cost_per_container ?? '',
      max_container_weight: tender.max_container_weight ?? '25',
      description: tender.description || '',
      app_start: isoToDatetimeLocal(tender.app_start),
      app_end: isoToDatetimeLocal(tender.app_end),
      bid_start: isoToDatetimeLocal(tender.bid_start),
      bid_end: isoToDatetimeLocal(tender.bid_end),
      step_type: tender.step_type || 'fixed',
      step_value: tender.step_value ?? '',
      step_from_own_bid: !!tender.step_from_own_bid,
      invited_emails: Array.isArray(tender.invited_emails) ? [...tender.invited_emails] : [],
      supplier_logistics: tender.supplier_logistics
        ? Object.fromEntries(Object.entries(tender.supplier_logistics).map(([k, v]) => [k, String(v)]))
        : {},
      // 1C ERP — импорт
      is_import: !!tender.is_import,
      duty_rate: tender.duty_rate ?? '',
      vat_rate: tender.vat_rate ?? '',
      customs_fee: tender.customs_fee ?? '',
      supplier_freight: tender.supplier_freight
        ? Object.fromEntries(Object.entries(tender.supplier_freight).map(([k, v]) => [k, String(v)]))
        : {},
      supplier_post_customs: tender.supplier_post_customs
        ? Object.fromEntries(Object.entries(tender.supplier_post_customs).map(([k, v]) => [k, String(v)]))
        : {},
      supplier_transit_days: tender.supplier_transit_days
        ? Object.fromEntries(Object.entries(tender.supplier_transit_days).map(([k, v]) => [k, String(v)]))
        : {},
      direction_override: tender.direction || '',
      allow_alternative_bids: !!tender.allow_alternative_bids,
      hide_participants: !!tender.hide_participants,
    });
    setActiveTab('create');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingTenderId(null);
    setLot({ ...emptyLot });
  };

  const handleDuplicateTender = async (tenderId) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/duplicate`);
      toast.success('Создана копия тендера (черновик)');
      await loadTenders();
      handleEditTender(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handlePlanUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Файл больше 5 МБ');
    const fd = new FormData();
    fd.append('file', file);
    setPlanLoading(true);
    setPlanResult(null);
    try {
      const { data } = await api.post('/tenders/plan-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPlanResult(data);
      const created = data.summary.created_count;
      const unresolvedProducts = data.summary.unresolved_products || 0;
      if (unresolvedProducts > 0) {
        toast.warning(`Создано черновиков: ${created}. Внимание: товар не найден в справочнике у ${unresolvedProducts} лотов.`);
      } else {
        toast.success(`Создано черновиков: ${created}`);
      }
      await loadTenders();
      setActiveTab('tenders');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setPlanLoading(false); }
  };

  const handlePublish = async (tenderId) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/publish`);
      toast.success(data.message);
      loadTenders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleSendResults = async (tender) => {
    const tenderId = typeof tender === 'string' ? tender : tender?.id;
    if (!tenderId) return;
    // Если торги ещё идут (фаза bidding) — обязательно подтверждение с явным
    // предупреждением, что это закроет торги досрочно.
    const phase = typeof tender === 'object' ? tender?.phase : null;
    if (phase === 'bidding') {
      const ok = window.confirm(
        'Внимание! Торги ещё идут. Отправка результатов сейчас закроет тендер ' +
        'ДОСРОЧНО — поставщики больше не смогут улучшать ставки.\n\nВы уверены?'
      );
      if (!ok) return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/send-results`);
      toast.success(data.message);
      loadTenders();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const viewBids = async (tender) => {
    setSelectedTender(tender);
    try {
      const { data } = await api.get(`/tenders/${tender.id}/bids`);
      setBids(data);
    } catch { setBids([]); }
  };

  // ====== Tender Actions ======
  const handleDeleteTender = async (tenderId) => {
    if (!window.confirm('Удалить тендер? Это действие необратимо.')) return;
    try {
      await api.delete(`/tenders/${tenderId}`);
      toast.success('Тендер удалён');
      loadTenders();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && typeof detail === 'string' && detail.includes('Обратная связь')) {
        // Дружелюбное модальное уведомление с переходом в «Обратную связь».
        toast.error(detail, {
          duration: 10000,
          action: {
            label: 'Открыть «Обратную связь»',
            onClick: () => {
              // Левое меню → пункт «Обратная связь» (роут зависит от реализации).
              window.location.href = '/?tab=feedback';
            },
          },
        });
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const handleCancelTender = async (tenderId) => {
    if (!window.confirm('Отменить тендер?')) return;
    try {
      await api.post(`/tenders/${tenderId}/cancel`);
      toast.success('Тендер отменён');
      loadTenders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleEarlyComplete = async (tenderId) => {
    if (!window.confirm('Досрочно завершить тендер и разослать результаты?')) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/tenders/${tenderId}/early-complete`);
      toast.success(data.message || 'Тендер досрочно завершён');
      loadTenders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleRenegotiate = async () => {
    if (!renego) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/tenders/${renego.tenderId}/renegotiate`, {
        bid_start: renego.bid_start,
        bid_end: renego.bid_end,
        additional_conditions: renego.additional_conditions || '',
      });
      toast.success(data.message || 'Переторжка назначена');
      setRenego(null);
      loadTenders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // ====== Supplier CRUD ======
  const handleAddSupplier = async () => {
    if (!newSupplier.email || !newSupplier.company_name) {
      toast.error('Укажите email и компанию');
      return;
    }
    try {
      const products = (newSupplier.supplier_products || '')
        .split(',').map(s => s.trim()).filter(Boolean);
      const payload = { ...newSupplier, supplier_products: products,
        supplier_kind: newSupplier.supplier_kind || null };
      await api.post('/suppliers', payload);
      toast.success('Поставщик добавлен');
      setNewSupplier({ company_name: '', email: '', contact_name: '', country: '', lang_code: 'RU', responsible: '', supplier_products: '', supplier_kind: '' });
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleSaveSupplier = async (email) => {
    if (!editingSupplier) return;
    try {
      const products = Array.isArray(editingSupplier.supplier_products)
        ? editingSupplier.supplier_products
        : (editingSupplier.supplier_products || '').split(',').map(s => s.trim()).filter(Boolean);
      const payload = { ...editingSupplier, supplier_products: products };
      await api.put(`/suppliers/${encodeURIComponent(email)}`, payload);
      toast.success('Сохранено');
      setEditingSupplier(null);
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDeleteSupplier = async (email) => {
    try {
      await api.delete(`/suppliers/${encodeURIComponent(email)}`);
      toast.success('Удалено');
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // ====== Supplier bulk import from Excel ======
  const handleDownloadSupplierTemplate = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/suppliers/bulk-template`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch { /* ignore */ }
        toast.error(detail || `Не удалось скачать шаблон (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const dispo = res.headers.get('content-disposition') || '';
      const match = dispo.match(/filename="([^"]+)"/);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = match ? match[1] : 'suppliers_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Ошибка загрузки');
    }
  };

  const handleImportSuppliersFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingSuppliers(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/suppliers/bulk-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const parts = [
        `Обработано строк: ${data.total_rows || 0}`,
        `создано: ${data.created || 0}`,
        `пропущено: ${data.skipped || 0}`,
        `ошибок: ${data.errors || 0}`,
      ];
      if (data.errors > 0 || data.skipped > 0) {
        const rowsInfo = [
          ...(data.details?.errors || []).map(r => `строка ${r.row} (${r.email || '—'}): ${r.reason}`),
          ...(data.details?.skipped || []).map(r => `строка ${r.row} (${r.email}): ${r.reason}`),
        ].slice(0, 6).join('\n');
        toast.warning(`${parts.join(', ')}${rowsInfo ? `\n${rowsInfo}` : ''}`, { duration: 12000 });
      } else {
        toast.success(parts.join(', '));
      }
      loadSuppliers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Не удалось импортировать');
    } finally {
      setImportingSuppliers(false);
    }
  };

  return (
    <div className="p-3" data-testid="organizer-dashboard">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Панель организатора</h1>
        <p className="text-sm text-slate-500 mt-1">Управление тендерами и поставщиками</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <LayoutDashboard className="w-4 h-4 mr-1.5" /> Обзор
          </TabsTrigger>
          <TabsTrigger value="create" data-testid="tab-create-lot">
            <FileText className="w-4 h-4 mr-1.5" /> Создание лота
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">
            <Users className="w-4 h-4 mr-1.5" /> Поставщики
          </TabsTrigger>
          <TabsTrigger value="tenders" data-testid="tab-tenders">
            <Settings className="w-4 h-4 mr-1.5" /> Тендеры
          </TabsTrigger>
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator className="w-4 h-4 mr-1.5" /> Калькулятор
          </TabsTrigger>
          <TabsTrigger value="catalog" data-testid="tab-catalog">
            <Package className="w-4 h-4 mr-1.5" /> Каталог товаров
          </TabsTrigger>
          {user?.is_super_admin && (
            <TabsTrigger value="archive" data-testid="tab-archive">
              <Archive className="w-4 h-4 mr-1.5" /> Архив
            </TabsTrigger>
          )}
        </TabsList>

        {/* TAB 0: Overview */}
        <TabsContent value="overview">
          <OverviewPanel onOpenTender={(t) => { setActiveTab('tenders'); setHighlightTenderId(t.id); viewBids(t); }} />
        </TabsContent>

        {/* TAB 1: Create Lot */}
        <TabsContent value="create">
          <form onSubmit={handleCreateLot} className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm" data-testid="create-lot-form">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900">
                  {editingTenderId ? 'Редактирование черновика' : 'Новый лот'}
                </h2>
                {editingTenderId && (
                  <p className="text-xs text-amber-700 mt-1">
                    Вы редактируете черновик. После публикации изменения будут недоступны.
                  </p>
                )}
              </div>
              {editingTenderId && (
                <Button type="button" variant="outline" size="sm" onClick={handleCancelEdit}
                  data-testid="cancel-edit-btn" className="rounded-sm text-xs">
                  <X className="w-3 h-3 mr-1" /> Отменить редактирование
                </Button>
              )}
            </div>
            <div className="form-grid-2col">
              {/* Left column */}
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Основные условия</p>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Наименование лота</Label>
                  <Input value={lot.lot_name} onChange={updateLot('lot_name')} required data-testid="lot-name" className="border-slate-300 rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Компания-закупщик</Label>
                  <Input value={lot.buying_company} onChange={updateLot('buying_company')} required data-testid="lot-buying-company" className="border-slate-300 rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  {isPurchaseOrg ? (
                    <ProductSelector
                      value={lot.product_name}
                      productId={lot.product_id}
                      onChange={({ name, name_en, id }) =>
                        setLot(p => ({ ...p, product_name: name, product_name_en: name_en, product_id: id }))
                      }
                      required
                    />
                  ) : (
                    <>
                      <Label className="text-sm font-medium text-slate-700">Закупаемый товар</Label>
                      <Input value={lot.product_name} onChange={updateLot('product_name')} required data-testid="lot-product" className="border-slate-300 rounded-sm" />
                    </>
                  )}
                </div>
                <div className="grid grid-cols-[1fr_110px] gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Количество</Label>
                    <Input type="number" step="0.01" value={lot.volume} onChange={updateLot('volume')} required data-testid="lot-volume" className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Ед. изм.</Label>
                    <Select value={lot.volume_unit} onValueChange={v => setLot(p => ({ ...p, volume_unit: v }))}>
                      <SelectTrigger data-testid="lot-volume-unit" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {units.map(u => <SelectItem key={u.code} value={u.code}>{u.code}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Описание лота</Label>
                  <Textarea value={lot.description} onChange={updateLot('description')} rows={3} data-testid="lot-description" className="border-slate-300 rounded-sm" />
                </div>

                <Separator />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Финансовые параметры</p>
                <div className="grid grid-cols-[1fr_110px] gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">
                      Ценовой потолок
                      {!priceCapRequired && <span className="ml-1 text-xs text-slate-400 font-normal">(необязательно)</span>}
                    </Label>
                    <Input type="number" step="0.01" value={lot.expected_price} onChange={updateLot('expected_price')}
                      required={priceCapRequired}
                      placeholder={priceCapRequired ? '' : 'Можно оставить пустым'}
                      data-testid="lot-expected-price" className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Валюта</Label>
                    <Select value={lot.currency} onValueChange={v => setLot(p => ({ ...p, currency: v }))}>
                      <SelectTrigger data-testid="lot-currency" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {!isLogisticsOrg && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Коэфф. %/день</Label>
                      <Input type="number" step="0.0001" value={lot.money_cost_day} onChange={updateLot('money_cost_day')} data-testid="lot-money-cost" className="border-slate-300 rounded-sm" />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Макс. вес конт. (т)</Label>
                    <Input type="number" step="0.01" value={lot.max_container_weight} onChange={updateLot('max_container_weight')} data-testid="lot-max-weight" className="border-slate-300 rounded-sm" />
                  </div>
                </div>
                {!isLogisticsOrg && (
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">
                      Логистическая надбавка{isPurchaseOrg ? ' (по умолчанию для логистики; в «Закупке» задаётся индивидуально для каждого поставщика ниже)' : ''}
                    </Label>
                    <Input type="number" step="0.01" value={lot.logistics_cost_per_container} onChange={updateLot('logistics_cost_per_container')} data-testid="lot-logistics-cost"
                      disabled={isPurchaseOrg}
                      className={`border-slate-300 rounded-sm ${isPurchaseOrg ? 'bg-slate-100 text-slate-400' : ''}`} />
                  </div>
                )}

                {/* === Супер-админ: выбор направления лота === */}
                {isSuperAdmin && (
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Направление лота</Label>
                    <Select
                      value={lot.direction_override || (user?.tender_direction || 'purchase')}
                      onValueChange={(v) => setLot(p => ({
                        ...p,
                        direction_override: v,
                        // Если уходим с purchase_oil — сбрасываем тоггл альтернатив.
                        allow_alternative_bids: v === 'purchase_oil' ? p.allow_alternative_bids : false,
                      }))}
                    >
                      <SelectTrigger data-testid="lot-direction-select" className="border-slate-300 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="purchase">Закупка</SelectItem>
                        <SelectItem value="purchase_oil">Закупка масла</SelectItem>
                        <SelectItem value="logistics">Логистика</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* === Альтернативные предложения (только «Закупка масла») === */}
                {(lot.direction_override === 'purchase_oil' || (isOilOrg && !lot.direction_override)) && (
                  <label className="flex items-center gap-2 cursor-pointer p-2 border border-emerald-200 bg-emerald-50/60 rounded-sm">
                    <input
                      type="checkbox"
                      checked={!!lot.allow_alternative_bids}
                      onChange={(e) => setLot(p => ({ ...p, allow_alternative_bids: e.target.checked }))}
                      data-testid="lot-allow-alternative-bids"
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm font-semibold text-emerald-900">Разрешить альтернативные предложения</span>
                    <span className="text-[11px] text-emerald-700 ml-1">(поставщик сможет подать несколько параллельных ставок: «Основная», «ЖД», и т.д.)</span>
                  </label>
                )}

                {/* === Скрыть участников (все направления) === */}
                <label className="flex items-center gap-2 cursor-pointer p-2 border border-slate-200 bg-slate-50/60 rounded-sm">
                  <input
                    type="checkbox"
                    checked={!!lot.hide_participants}
                    onChange={(e) => setLot(p => ({ ...p, hide_participants: e.target.checked }))}
                    data-testid="lot-hide-participants"
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm font-semibold text-slate-900">Скрыть участников</span>
                  <span className="text-[11px] text-slate-600 ml-1">(в ранжировании поставщики увидят только свою ставку и своё место — количество конкурентов и их цены будут скрыты)</span>
                </label>

                {/* === 1C ERP: Импортные параметры (только для «Закупки») === */}
                {isPurchaseOrg && (
                  <div className="mt-2 border border-slate-200 rounded-sm p-3 bg-slate-50/60 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!lot.is_import}
                        onChange={(e) => setLot(p => ({ ...p, is_import: e.target.checked }))}
                        data-testid="lot-is-import"
                        className="rounded border-slate-300"
                      />
                      <span className="text-sm font-semibold text-slate-700">Импортный лот (1C ERP)</span>
                    </label>
                    {lot.is_import && (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-600">Пошлина, %</Label>
                            <Input type="number" step="0.01" value={lot.duty_rate}
                              onChange={updateLot('duty_rate')} data-testid="lot-duty-rate"
                              className="h-8 text-sm border-slate-300 rounded-sm" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-600">НДС, %</Label>
                            <Input type="number" step="0.01" value={lot.vat_rate}
                              onChange={updateLot('vat_rate')} data-testid="lot-vat-rate"
                              className="h-8 text-sm border-slate-300 rounded-sm" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-600">Тамож. сбор</Label>
                            <Input type="number" step="0.01" value={lot.customs_fee}
                              onChange={updateLot('customs_fee')} data-testid="lot-customs-fee"
                              className="h-8 text-sm border-slate-300 rounded-sm" />
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Фрахт, доставка после таможни и срок в пути задаются индивидуально по каждому поставщику в блоке «Участники» ниже.
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Шаг торга</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Тип шага</Label>
                    <Select value={lot.step_type} onValueChange={v => setLot(p => ({ ...p, step_type: v }))}>
                      <SelectTrigger data-testid="lot-step-type" className="border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Фиксированная сумма</SelectItem>
                        <SelectItem value="percent">Процент (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Значение шага</Label>
                    <div className="flex items-center gap-3">
                      <Input type="number" step="0.01" value={lot.step_value} onChange={updateLot('step_value')} data-testid="lot-step-value" className="border-slate-300 rounded-sm flex-1" />
                      <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={!!lot.step_from_own_bid}
                          onChange={(e) => setLot(p => ({ ...p, step_from_own_bid: e.target.checked }))}
                          data-testid="lot-step-from-own-bid"
                          className="rounded border-slate-300"
                        />
                        от ставки
                      </label>
                    </div>
                  </div>
                </div>

                <Separator />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Временные рамки <span className="text-[#0046AD] normal-case tracking-normal">(московское время)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Начало заявок (МСК)</Label>
                    <Input type="datetime-local" value={lot.app_start} onChange={updateLot('app_start')} required data-testid="lot-app-start" className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Конец заявок (МСК)</Label>
                    <Input type="datetime-local" value={lot.app_end} onChange={updateLot('app_end')} required data-testid="lot-app-end" className="border-slate-300 rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Начало торгов (МСК)</Label>
                    <Input type="datetime-local" value={lot.bid_start} onChange={updateLot('bid_start')} required data-testid="lot-bid-start" className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Конец торгов (МСК)</Label>
                    <Input type="datetime-local" value={lot.bid_end} onChange={updateLot('bid_end')} required data-testid="lot-bid-end" className="border-slate-300 rounded-sm" />
                  </div>
                </div>

                <Separator />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Участники</p>

                {/* Поиск по поставщикам */}
                <Input placeholder="Поиск поставщика..." value={supplierSearch}
                  onChange={e => setSupplierSearch(e.target.value)}
                  className="border-slate-300 rounded-sm text-sm" data-testid="supplier-search-lot" />

                {/* Select existing suppliers */}
                <div className="space-y-2 max-h-36 overflow-y-auto border border-slate-200 rounded-sm p-3 bg-slate-50">
                  {suppliers.length === 0 && <p className="text-xs text-slate-400">Нет поставщиков. Добавьте ниже.</p>}
                  {suppliers
                    .filter(s => !s.stop_list)
                    .filter(s => {
                      if (!supplierSearch) return true;
                      const q = supplierSearch.toLowerCase();
                      return (s.company_name||'').toLowerCase().includes(q)
                        || (s.email||'').toLowerCase().includes(q)
                        || (s.contact_name||'').toLowerCase().includes(q);
                    })
                    .map(s => {
                      const checked = lot.invited_emails.includes(s.email);
                      const slKey = (s.email || '').toLowerCase();
                      const slVal = lot.supplier_logistics?.[slKey] ?? '';
                      const slMissing = (isPurchaseOrg || isOilOrg) && !lot.is_import && checked && (slVal === '' || parseFloat(slVal) <= 0);
                      return (
                    <div key={s.email} className="flex items-center gap-2 text-sm hover:bg-white px-1 py-0.5 rounded">
                      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEmail(s.email)}
                          className="rounded border-slate-300"
                        />
                        <span className="text-slate-700 truncate">{s.company_name}</span>
                        <span className="text-slate-400 text-xs truncate">({s.email})</span>
                        <span className={`text-[10px] font-bold px-1 rounded ${s.lang_code === 'ENG' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{s.lang_code || 'RU'}</span>
                      </label>
                      {(isPurchaseOrg || isOilOrg) && !lot.is_import && checked && (
                        <Input
                          type="number" step="0.01" min="0"
                          placeholder="C, лог."
                          value={slVal}
                          onChange={(e) => setSupplierLogistics(s.email, e.target.value)}
                          data-testid={`supplier-logistics-${s.email}`}
                          className={`h-7 w-24 text-xs border-slate-300 rounded-sm ${slMissing ? 'border-rose-400 bg-rose-50' : ''}`}
                        />
                      )}
                    </div>
                      );
                    })}
                </div>

                {/* Add new supplier inline — УДАЛЕНО: добавление новых поставщиков только через справочник «Поставщики». */}

                {/* === 1C ERP: Импортные параметры по поставщикам (только для «Закупки» с is_import=true) === */}
                {isPurchaseOrg && lot.is_import && (() => {
                  const existingSuppliers = (suppliers || []).filter(s => lot.invited_emails.includes(s.email));
                  const allRows = existingSuppliers.map(s => ({ email: s.email, company_name: s.company_name, lang_code: s.lang_code }));
                  if (allRows.length === 0) {
                    return (
                      <div className="border border-sky-200 bg-sky-50/60 rounded-sm p-3 text-xs text-sky-800">
                        Импортный режим включён. Сначала выберите поставщиков выше, затем здесь появятся индивидуальные поля: Фрахт / После таможни / Дни в пути.
                      </div>
                    );
                  }
                  return (
                    <div className="border border-sky-200 bg-sky-50/60 rounded-sm p-3 space-y-2">
                      <p className="text-xs font-semibold text-sky-900 flex items-center gap-1.5">
                        <span>Импортные параметры по поставщикам</span>
                        <span className="text-[10px] text-sky-700 font-normal">(валюта лота, на партию)</span>
                      </p>
                      <div className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-2 text-[10px] uppercase tracking-wider text-sky-800 font-semibold pb-1 border-b border-sky-200">
                        <span>Поставщик</span>
                        <span>Фрахт</span>
                        <span>После таможни</span>
                        <span>Дни в пути</span>
                      </div>
                      {allRows.map(s => {
                        const slKey = (s.email || '').toLowerCase();
                        const sfVal = lot.supplier_freight?.[slKey] ?? '';
                        const spVal = lot.supplier_post_customs?.[slKey] ?? '';
                        const stVal = lot.supplier_transit_days?.[slKey] ?? '';
                        return (
                          <div key={s.email} className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-2 items-center">
                            <div className="text-xs text-slate-700 min-w-0">
                              <div className="truncate font-medium">{s.company_name || s.email}</div>
                              <div className="truncate text-[10px] text-slate-400">{s.email}</div>
                            </div>
                            <Input type="number" step="0.01" min="0" placeholder="0.00"
                              value={sfVal}
                              onChange={(e) => setSupplierFreight(s.email, e.target.value)}
                              data-testid={`supplier-freight-${s.email}`}
                              className="h-8 text-xs border-slate-300 rounded-sm bg-white" />
                            <Input type="number" step="0.01" min="0" placeholder="0.00"
                              value={spVal}
                              onChange={(e) => setSupplierPostCustoms(s.email, e.target.value)}
                              data-testid={`supplier-post-customs-${s.email}`}
                              className="h-8 text-xs border-slate-300 rounded-sm bg-white" />
                            <Input type="number" step="1" min="0" placeholder="0"
                              value={stVal}
                              onChange={(e) => setSupplierTransitDays(s.email, e.target.value)}
                              data-testid={`supplier-transit-${s.email}`}
                              className="h-8 text-xs border-slate-300 rounded-sm bg-white" />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button type="submit" disabled={loading} data-testid="create-lot-submit"
                className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
                {editingTenderId ? (
                  <><Save className="w-4 h-4 mr-2" /> {loading ? 'Сохранение...' : 'Сохранить изменения'}</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> {loading ? 'Создание...' : 'Создать лот'}</>
                )}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* TAB 2: Supplier Directory */}
        <TabsContent value="suppliers">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="supplier-directory">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Справочник поставщиков</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="search"
                    placeholder="Поиск: компания, email, ФИО, страна, ответственный…"
                    value={directorySearch}
                    onChange={e => setDirectorySearch(e.target.value)}
                    data-testid="suppliers-search-input"
                    className="h-8 pl-7 pr-2 text-xs border-slate-300 rounded-sm w-72" />
                </div>
                <span className="text-xs text-slate-500 mr-2" data-testid="suppliers-count-label">
                  {directorySearch
                    ? `${filteredSuppliers.length} / ${suppliers.length}`
                    : `${suppliers.length} записей`}
                </span>
                <Button variant="outline" size="sm" onClick={handleDownloadSupplierTemplate}
                  data-testid="suppliers-template-btn"
                  className="h-8 px-3 text-xs border-slate-300 rounded-sm">
                  Скачать шаблон
                </Button>
                <label className="inline-flex items-center gap-1 h-8 px-3 text-xs border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-sm cursor-pointer"
                  data-testid="suppliers-import-label">
                  {importingSuppliers ? 'Загрузка…' : 'Импорт из Excel'}
                  <input type="file" accept=".xlsx" hidden onChange={handleImportSuppliersFile}
                    data-testid="suppliers-import-input" disabled={importingSuppliers} />
                </label>
              </div>
            </div>

            {/* Add new supplier form */}
            <div className="p-4 border-b border-slate-100 bg-slate-50/50">
              <div className="flex gap-2 items-end flex-wrap">
                <Input placeholder="Компания *" value={newSupplier.company_name}
                  onChange={e => setNewSupplier(p => ({ ...p, company_name: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm flex-1 min-w-[140px]" data-testid="dir-supplier-company" />
                <Input placeholder="Email *" type="email" value={newSupplier.email}
                  onChange={e => setNewSupplier(p => ({ ...p, email: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm flex-1 min-w-[140px]" data-testid="dir-supplier-email" />
                <Input placeholder="ФИО" value={newSupplier.contact_name}
                  onChange={e => setNewSupplier(p => ({ ...p, contact_name: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm flex-1 min-w-[120px]" data-testid="dir-supplier-name" />
                <Input placeholder="Страна" value={newSupplier.country}
                  onChange={e => setNewSupplier(p => ({ ...p, country: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm w-28" data-testid="dir-supplier-country" />
                <Input placeholder="Ответственный" value={newSupplier.responsible}
                  onChange={e => setNewSupplier(p => ({ ...p, responsible: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm flex-1 min-w-[140px]" data-testid="dir-supplier-responsible" />
                <Input placeholder="Поставляемые продукты (через запятую)"
                  value={newSupplier.supplier_products}
                  onChange={e => setNewSupplier(p => ({ ...p, supplier_products: e.target.value }))}
                  className="border-slate-300 rounded-sm text-sm flex-1 min-w-[180px]"
                  data-testid="dir-supplier-products" />
                {isPurchaseDir && (
                  <Select value={newSupplier.supplier_kind || 'none'}
                    onValueChange={v => setNewSupplier(p => ({ ...p, supplier_kind: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="dir-supplier-kind"
                      className="border-slate-300 rounded-sm text-sm w-40">
                      <SelectValue placeholder="Вид поставщика" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— не указан —</SelectItem>
                      <SelectItem value="manufacturer">Производитель</SelectItem>
                      <SelectItem value="trader">Трейдер</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Select value={newSupplier.lang_code} onValueChange={v => setNewSupplier(p => ({ ...p, lang_code: v }))}>
                  <SelectTrigger data-testid="dir-supplier-lang" className="border-slate-300 rounded-sm text-sm w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RU">RU</SelectItem>
                    <SelectItem value="ENG">ENG</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddSupplier} size="sm" data-testid="dir-add-supplier"
                  className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm shrink-0">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Компания</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-40">Направление</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">ФИО</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Страна</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ответственный</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Поставляемые продукты</TableHead>
                    {isPurchaseDir && (
                      <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-32">Вид</TableHead>
                    )}
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-16">ISO</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSuppliers.map(s => {
                    const isEditing = editingSupplier?._email === s.email;
                    const isStopped = !!s.stop_list;
                    const isUnassigned = !isStopped && !s.assigned_organizer && s.supplier_direction === 'purchase';
                    const rowCls = isStopped
                      ? 'bg-rose-50/40 hover:bg-rose-50/60'
                      : (isUnassigned ? 'bg-amber-50/40 hover:bg-amber-50/60' : 'hover:bg-slate-50/50');
                    return (
                    <TableRow key={s.email} className={`border-b border-slate-100 ${rowCls} group`}>
                      <TableCell>
                        {isEditing
                          ? <Input value={editingSupplier.company_name} onChange={e => setEditingSupplier(p => ({...p, company_name: e.target.value}))} className="border-slate-300 rounded-sm text-sm h-8" />
                          : (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-sm font-medium text-slate-900">{s.company_name}</span>
                              {isStopped && (
                                <span data-testid={`supplier-stoplist-badge-${s.email}`}
                                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">
                                  В стоп-листе
                                </span>
                              )}
                              {isUnassigned && (
                                <span data-testid={`supplier-unassigned-badge-${s.email}`}
                                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                  Не распределён
                                </span>
                              )}
                            </div>
                          )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{s.email}</TableCell>
                      <TableCell>
                        {isEditing && isSuperAdmin ? (
                          <Select value={editingSupplier.supplier_direction || 'purchase'}
                            onValueChange={v => setEditingSupplier(p => ({...p, supplier_direction: v}))}>
                            <SelectTrigger data-testid={`edit-direction-${s.email}`} className="border-slate-300 rounded-sm text-sm h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="purchase">Закупка</SelectItem>
                              <SelectItem value="purchase_oil">Закупка масла</SelectItem>
                              <SelectItem value="logistics">Логистика</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (() => {
                          const dir = s.supplier_direction || '';
                          const label = dir === 'purchase' ? 'Закупка'
                            : dir === 'purchase_oil' ? 'Закупка масла'
                            : dir === 'logistics' ? 'Логистика' : '—';
                          const cls = dir === 'purchase' ? 'bg-blue-100 text-blue-700 border-blue-200'
                            : dir === 'purchase_oil' ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : dir === 'logistics' ? 'bg-slate-100 text-slate-700 border-slate-200'
                            : 'bg-slate-50 text-slate-400 border-slate-200';
                          return (
                            <span data-testid={`supplier-direction-${s.email}`}
                              className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${cls}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {isEditing
                          ? <Input value={editingSupplier.contact_name} onChange={e => setEditingSupplier(p => ({...p, contact_name: e.target.value}))} className="border-slate-300 rounded-sm text-sm h-8" />
                          : <span className="text-sm text-slate-600">{s.contact_name}</span>}
                      </TableCell>
                      <TableCell>
                        {isEditing
                          ? <Input value={editingSupplier.country} onChange={e => setEditingSupplier(p => ({...p, country: e.target.value}))} className="border-slate-300 rounded-sm text-sm h-8 w-24" />
                          : <span className="text-sm text-slate-600">{s.country}</span>}
                      </TableCell>
                      <TableCell>
                        {isEditing
                          ? (
                              <Select
                                value={editingSupplier.assigned_organizer || 'none'}
                                onValueChange={v => {
                                  if (v === 'none') {
                                    setEditingSupplier(p => ({ ...p, assigned_organizer: null, responsible: '' }));
                                    return;
                                  }
                                  const org = organizers.find(o => o.id === v);
                                  setEditingSupplier(p => ({
                                    ...p,
                                    assigned_organizer: v,
                                    responsible: org ? org.name : p.responsible,
                                  }));
                                }}>
                                <SelectTrigger className="border-slate-300 rounded-sm text-sm h-8" data-testid={`edit-responsible-${s.email}`}>
                                  <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">—</SelectItem>
                                  {organizers.map(o => (
                                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )
                          : <span className="text-sm text-slate-600">{s.responsible || '—'}</span>}
                      </TableCell>
                      <TableCell>
                        {isEditing
                          ? <Input value={Array.isArray(editingSupplier.supplier_products) ? editingSupplier.supplier_products.join(', ') : (editingSupplier.supplier_products || '')}
                              onChange={e => setEditingSupplier(p => ({...p, supplier_products: e.target.value}))}
                              placeholder="через запятую"
                              className="border-slate-300 rounded-sm text-sm h-8" data-testid={`edit-products-${s.email}`} />
                          : <span className="text-sm text-slate-600">
                              {Array.isArray(s.supplier_products) && s.supplier_products.length
                                ? s.supplier_products.join(', ')
                                : '—'}
                            </span>}
                      </TableCell>
                      {isPurchaseDir && (
                        <TableCell>
                          {isEditing
                            ? (
                              <Select value={editingSupplier.supplier_kind || 'none'}
                                onValueChange={v => setEditingSupplier(p => ({...p, supplier_kind: v === 'none' ? null : v}))}>
                                <SelectTrigger data-testid={`edit-kind-${s.email}`} className="border-slate-300 rounded-sm text-sm h-8"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">—</SelectItem>
                                  <SelectItem value="manufacturer">Производитель</SelectItem>
                                  <SelectItem value="trader">Трейдер</SelectItem>
                                </SelectContent>
                              </Select>
                            )
                            : (
                              <span className="text-sm text-slate-600">
                                {s.supplier_kind === 'manufacturer' ? 'Производитель'
                                  : s.supplier_kind === 'trader' ? 'Трейдер' : '—'}
                              </span>
                            )}
                        </TableCell>
                      )}
                      <TableCell>
                        {isEditing
                          ? <Select value={editingSupplier.lang_code || 'RU'} onValueChange={v => setEditingSupplier(p => ({...p, lang_code: v}))}>
                              <SelectTrigger className="border-slate-300 rounded-sm text-sm h-8 w-16"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="RU">RU</SelectItem><SelectItem value="ENG">ENG</SelectItem></SelectContent>
                            </Select>
                          : <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${(s.lang_code||'RU') === 'ENG' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>{s.lang_code || 'RU'}</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {isSuperAdmin && (
                          <div className="mb-1 flex flex-col items-end gap-0.5">
                            {isEditing ? (
                              <>
                                <label className="flex items-center gap-1 text-[11px] text-slate-700 cursor-pointer">
                                  <input type="checkbox"
                                    checked={!!editingSupplier.stop_list}
                                    onChange={e => setEditingSupplier(p => ({ ...p, stop_list: e.target.checked }))}
                                    data-testid={`edit-stoplist-${s.email}`}
                                    className="rounded border-slate-300" />
                                  <span>В стоп-листе</span>
                                </label>
                                {editingSupplier.stop_list && (
                                  <Input value={editingSupplier.stop_list_comment || ''}
                                    onChange={e => setEditingSupplier(p => ({ ...p, stop_list_comment: e.target.value }))}
                                    placeholder="Комментарий"
                                    data-testid={`edit-stoplist-comment-${s.email}`}
                                    className="h-7 text-xs border-slate-300 rounded-sm w-44" />
                                )}
                              </>
                            ) : (
                              s.stop_list_comment && (
                                <span title={s.stop_list_comment} className="text-[10px] text-rose-500 max-w-[10rem] truncate">
                                  {s.stop_list_comment}
                                </span>
                              )
                            )}
                          </div>
                        )}
                        {isEditing ? (
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => handleSaveSupplier(s.email)} className="text-emerald-600 border-emerald-200 h-7 px-2"><Save className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingSupplier(null)} className="text-slate-400 h-7 px-2"><X className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" onClick={() => setEditingSupplier({ _email: s.email, company_name: s.company_name, contact_name: s.contact_name, country: s.country, lang_code: s.lang_code || 'RU', responsible: s.responsible || '', supplier_products: Array.isArray(s.supplier_products) ? s.supplier_products.join(', ') : (s.supplier_products || ''), supplier_kind: s.supplier_kind || null, supplier_direction: s.supplier_direction || 'purchase', stop_list: !!s.stop_list, stop_list_comment: s.stop_list_comment || '', assigned_organizer: s.assigned_organizer || null })}
                              className="text-slate-400 hover:text-[#0046AD] h-7 px-2" data-testid={`edit-supplier-${s.email}`}>
                              <Settings className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteSupplier(s.email)}
                              className="text-rose-500 hover:text-rose-700 h-7 px-2" data-testid={`delete-supplier-${s.email}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );})}
                  {suppliers.length === 0 && (
                    <TableRow><TableCell colSpan={isPurchaseDir ? 10 : 9} className="text-center text-slate-400 text-sm py-8">Поставщики не добавлены</TableCell></TableRow>
                  )}
                  {suppliers.length > 0 && filteredSuppliers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isPurchaseDir ? 10 : 9} className="text-center text-slate-400 text-sm py-8" data-testid="suppliers-empty-search">
                        Ничего не найдено по запросу «{directorySearch}»
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* TAB 3: Tender Management */}
        <TabsContent value="tenders">
          <div className="space-y-4" data-testid="tender-management">
            {/* Search + Plan Upload */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Поиск тендеров..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-9 border-slate-300 rounded-sm" data-testid="search-tenders" />
              </div>
              <Button variant="outline" size="sm" onClick={loadTenders} className="rounded-sm">Обновить</Button>
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-sm border border-[#0046AD]/30 text-[#0046AD] bg-[#0046AD]/5 hover:bg-[#0046AD]/10 cursor-pointer text-sm font-semibold"
                data-testid="plan-upload-btn">
                <Upload className="w-4 h-4" />
                {planLoading ? 'Загрузка...' : 'Загрузить план (Excel/CSV)'}
                <input type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden"
                  onChange={handlePlanUpload} disabled={planLoading} />
              </label>
              <a href="/plan_template.csv" download
                className="text-xs text-slate-500 hover:text-[#0046AD] underline underline-offset-2">
                скачать шаблон
              </a>
            </div>

            {/* Plan upload result summary */}
            {planResult && (
              <div className="bg-[#0046AD]/5 border border-[#0046AD]/20 rounded-sm p-3" data-testid="plan-result">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="text-sm font-semibold text-[#0046AD]">
                    Загрузка плана: создано {planResult.summary.created_count} из {planResult.summary.total_rows} строк
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setPlanResult(null)} className="h-6 w-6 p-0">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                {planResult.summary.unresolved_participants > 0 && (
                  <div className="text-xs text-amber-700 flex items-start gap-1 mt-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    Не удалось сопоставить участников: {planResult.summary.unresolved_participants} (указаны не как email и не найдены в справочнике поставщиков).
                    Допишите их вручную при редактировании лота.
                  </div>
                )}
                {planResult.summary.unresolved_products > 0 && (
                  <div className="text-xs text-amber-700 flex items-start gap-1 mt-2 p-2 bg-amber-100/70 rounded-sm">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold mb-1">
                        Товар не найден в справочнике в {planResult.summary.unresolved_products} лотах.
                        Поле «Закупаемый товар» осталось пустым — заполните вручную или попросите супер-админа добавить товар в справочник.
                      </div>
                      <ul className="list-disc list-inside text-[11px] space-y-0.5">
                        {(planResult.products_unresolved || []).slice(0, 30).map((p, i) => (
                          <li key={i}>
                            <span className="font-medium">{p.lot_name || `Строка ${p.row_number}`}</span>
                            : «{p.product_name}»
                          </li>
                        ))}
                        {(planResult.products_unresolved || []).length > 30 && (
                          <li className="text-amber-600">…и ещё {planResult.products_unresolved.length - 30}</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-rose-600 mt-2 font-semibold">
                  Важно: при автоматической обработке плана могут быть допущены ошибки. Проверьте каждый черновик перед запуском.
                </p>
              </div>
            )}

            {/* Tender List */}
            {sortedTenders.map(t => {
              const phase = PHASE_MAP[t.phase] || PHASE_MAP.draft;
              const isOwner = t.is_owner !== false; // владелец (для пометки «чужой»)
              const canManage = t.can_manage !== false; // право редактировать/публиковать/decide
              const isHighlighted = highlightTenderId === t.id;
              return (
                <div
                  key={t.id}
                  ref={el => { tenderRefs.current[t.id] = el; }}
                  className={`bg-white border rounded-sm shadow-sm hover-lift transition-all duration-300 ${
                    isHighlighted
                      ? 'border-[#0046AD] ring-2 ring-[#0046AD]/40 bg-[#0046AD]/[0.03]'
                      : 'border-slate-200'
                  }`}
                  data-testid={`tender-card-${t.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-heading text-base font-semibold text-slate-900">
                          {t.lot_name}
                          {!isOwner && (
                            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">чужой</span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">{t.product_name} | {t.volume} {t.volume_unit || 'т'} | {t.currency}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`phase-badge ${phase.cls}`}>{phase.label}</Badge>
                        <Badge variant="outline" className="text-xs">{t.bid_count || 0} ставок</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-xs text-slate-500 mt-3">
                      <div><Clock className="w-3 h-3 inline mr-1" />Заявки: {formatDateMSK(t.app_start)} - {formatDateMSK(t.app_end)}</div>
                      <div><Clock className="w-3 h-3 inline mr-1" />Торги: {formatDateMSK(t.bid_start)} - {formatDateMSK(t.bid_end)}</div>
                      {Number(t.expected_price) > 0 && (
                        <div>Ценовой потолок: {formatCurrency(t.expected_price, t.currency)}</div>
                      )}
                      <div>Приглашено: {t.invited_emails?.length || 0}</div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {canManage && t.status === 'draft' && (
                        <>
                          <Button size="sm" onClick={() => handlePublish(t.id)} disabled={loading} data-testid={`publish-tender-${t.id}`}
                            className="bg-[#0046AD] hover:bg-[#003A8C] rounded-sm text-xs">
                            <Send className="w-3 h-3 mr-1" /> Опубликовать
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEditTender(t)} data-testid={`edit-tender-${t.id}`}
                            className="rounded-sm text-xs text-[#0046AD] border-[#0046AD]/30 hover:bg-[#0046AD]/5">
                            <Pencil className="w-3 h-3 mr-1" /> Редактировать
                          </Button>
                        </>
                      )}
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => handleDuplicateTender(t.id)} disabled={loading}
                          data-testid={`duplicate-tender-${t.id}`} className="rounded-sm text-xs text-slate-700 border-slate-300 hover:bg-slate-50">
                          <Copy className="w-3 h-3 mr-1" /> Копировать
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => viewBids(t)} data-testid={`view-bids-${t.id}`}
                        className="rounded-sm text-xs">
                        <Eye className="w-3 h-3 mr-1" /> Ставки
                      </Button>
                      {canManage && t.phase === 'completed' && (
                        <Button size="sm" variant="outline" onClick={() => handleSendResults(t)} disabled={loading}
                          data-testid={`send-results-${t.id}`} className="rounded-sm text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                          <Award className="w-3 h-3 mr-1" /> Результаты
                        </Button>
                      )}
                      {canManage && t.phase !== 'completed' && t.phase !== 'cancelled' && t.status !== 'draft' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setShiftDates(t)}
                            data-testid={`shift-dates-${t.id}`} className="rounded-sm text-xs text-blue-700 border-blue-200 hover:bg-blue-50">
                            <CalendarClock className="w-3 h-3 mr-1" /> Сдвинуть сроки
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEarlyComplete(t.id)} disabled={loading}
                            data-testid={`early-complete-${t.id}`} className="rounded-sm text-xs text-amber-700 border-amber-200 hover:bg-amber-50">
                            <FastForward className="w-3 h-3 mr-1" /> Завершить досрочно
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleCancelTender(t.id)}
                            data-testid={`cancel-tender-${t.id}`} className="rounded-sm text-xs text-rose-600 border-rose-200 hover:bg-rose-50">
                            <Ban className="w-3 h-3 mr-1" /> Отменить
                          </Button>
                        </>
                      )}
                      {canManage && (t.phase === 'completed') && (
                        <Button size="sm" variant="outline" onClick={() => setRenego({ tenderId: t.id, bid_start: '', bid_end: '', additional_conditions: '' })}
                          data-testid={`renegotiate-${t.id}`} className="rounded-sm text-xs text-orange-700 border-orange-200 hover:bg-orange-50">
                          <RefreshCw className="w-3 h-3 mr-1" /> Переторжка
                        </Button>
                      )}
                      {canManage && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteTender(t.id)}
                          data-testid={`delete-tender-${t.id}`} className="rounded-sm text-xs text-rose-500 border-rose-200 hover:bg-rose-50">
                          <Trash2 className="w-3 h-3 mr-1" /> Удалить
                        </Button>
                      )}
                    </div>
                    <TenderAttachments tenderId={t.id} canEdit={canManage} />
                    <TenderMonitor tender={t} />
                    {canManage && t.status !== 'draft' && (
                      <InvitationLinksPanel tender={t} />
                    )}
                    {user?.is_super_admin && t.status !== 'draft' && (
                      <AdminInvitationsLog tenderId={t.id} />
                    )}
                  </div>
                </div>
              );
            })}

            {tenders.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Тендеры не найдены</p>
              </div>
            )}

            {/* Bid Monitoring Modal */}
            {selectedTender && (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm mt-4" data-testid="bid-monitoring">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-base font-semibold text-slate-900">
                        Мониторинг: {selectedTender.lot_name}
                      </h3>
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded ${isConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`} data-testid="ws-monitor-status">
                        {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                        {isConnected ? 'LIVE' : 'Offline'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{bids.length} ставок</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedTender(null)} className="text-slate-400">
                    Закрыть
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Место</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Компания</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Цена/т</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Вес/конт.</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">U (лог.)</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">E (фин.)</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Приведённая</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Условия</TableHead>
                        <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Комментарий</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bids.map(b => (
                        <TableRow key={b.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <TableCell>
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${b.rank === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                              {b.rank}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium text-slate-900">{b.company_name || '-'}</div>
                            <div className="text-xs text-slate-400">{b.user_email}</div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-medium">{b.price_per_ton?.toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{b.weight_in_container?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            <span className="indicator-badge bg-indigo-50 text-indigo-700 border border-indigo-200">{b.indicator_U?.toFixed(2)}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="indicator-badge bg-cyan-50 text-cyan-700 border border-cyan-200">{b.indicator_E?.toFixed(2)}</span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-bold text-slate-900">{b.adjusted_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            Пред. {b.prepay_percent}%/{b.prepay_days}д | Отср. {b.delay_percent}%/{b.delay_days}д
                          </TableCell>
                          <TableCell className="text-xs text-slate-600 max-w-[200px]">
                            {b.comment ? <span className="italic">{b.comment}</span> : <span className="text-slate-300">—</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {bids.length === 0 && (
                        <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Ставок пока нет</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <ShiftDatesDialog
              tender={shiftDates}
              open={!!shiftDates}
              onClose={() => setShiftDates(null)}
              onSaved={() => { setShiftDates(null); loadTenders(); }}
            />

            {/* Renegotiation Modal */}
            {renego && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="renegotiation-modal">
                <div className="bg-white rounded-sm shadow-lg w-full max-w-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">Назначить переторжку</h3>
                    <Button variant="ghost" size="sm" onClick={() => setRenego(null)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Начало торгов (МСК)</Label>
                      <Input type="datetime-local" value={renego.bid_start}
                        onChange={e => setRenego(p => ({ ...p, bid_start: e.target.value }))}
                        required data-testid="renego-bid-start" className="border-slate-300 rounded-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Конец торгов (МСК)</Label>
                      <Input type="datetime-local" value={renego.bid_end}
                        onChange={e => setRenego(p => ({ ...p, bid_end: e.target.value }))}
                        required data-testid="renego-bid-end" className="border-slate-300 rounded-sm" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium text-slate-700">Дополнительные условия <span className="text-slate-400 font-normal">(необязательно)</span></Label>
                      <Textarea value={renego.additional_conditions}
                        onChange={e => setRenego(p => ({ ...p, additional_conditions: e.target.value }))}
                        rows={3} placeholder="Укажите дополнительные условия переторжки..."
                        data-testid="renego-conditions" className="border-slate-300 rounded-sm" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setRenego(null)} className="rounded-sm">Отмена</Button>
                      <Button onClick={handleRenegotiate} disabled={loading || !renego.bid_start || !renego.bid_end}
                        data-testid="renego-submit" className="bg-orange-600 hover:bg-orange-700 rounded-sm">
                        <RefreshCw className="w-4 h-4 mr-2" /> {loading ? 'Назначение...' : 'Назначить переторжку'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* TAB: Calculator */}
        <TabsContent value="calculator">
          <PriceCalculator />
        </TabsContent>

        {/* TAB: Каталог товаров (доступен всем организаторам; полные права у super-admin) */}
        <TabsContent value="catalog">
          <ProductsManager canManage={!!user?.is_super_admin} />
        </TabsContent>

        {/* TAB: Archive (super-admin only) */}
        {user?.is_super_admin && (
          <TabsContent value="archive">
            <ArchivePanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
